import './BasicEntryContent.css';

import { useEffect, useRef, useState } from 'react';

import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { useModalKeyboard } from '../../../../../hooks/shortcuts/useModalKeyboard';
import { getCursorPosition } from '../../../../../utils/forms/inputs/getCursorPosition';
import { validateEntryValue } from '../../utils/validateEntryValue';
import { sanitizeEntryNumericValue } from '../../utils/sanitizeEntryNumericValue';
import Input from '../../../../Input/Input';
import EntryActionButtons from '../EntryActionButtons/EntryActionButtons';

export interface BasicEntryContentProps {
  valueType: 'string' | 'int' | 'float';
  defaultValue?: string | number;
  isSubmitting: boolean;
  handleAction: (action: () => void | Promise<void>) => void | Promise<void>;
  submitEntryValue: (value: string) => void | Promise<void>;
  cancelModalAction: () => void | Promise<void>;
}

// Virtual keyboard hints per entered value type.
const INPUT_MODES: Record<
  BasicEntryContentProps['valueType'],
  'text' | 'numeric' | 'decimal'
> = {
  string: 'text',
  int: 'numeric',
  float: 'decimal',
};

const BasicEntryContent = ({
  valueType,
  defaultValue,
  isSubmitting,
  handleAction,
  submitEntryValue,
  cancelModalAction,
}: BasicEntryContentProps) => {
  // Numeric defaults go through the same sanitization as typed input, so a
  // dot-decimal default renders with the display comma. The server may send
  // the default as a number or a string; 0 is a valid default.
  const [value, setValue] = useState<string>(() => {
    if (defaultValue === undefined || defaultValue === null) {
      return '';
    }
    const stringDefault = String(defaultValue);
    if (valueType === 'string') {
      return stringDefault;
    }
    return sanitizeEntryNumericValue(valueType, stringDefault, '');
  });

  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslate();
  const validation = validateEntryValue(valueType, value);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = () => {
    if (!validation.isValid) {
      return;
    }

    handleAction(() => submitEntryValue(validation.normalizedValue));
  };

  useModalKeyboard({
    onEnter: validation.isValid ? submit : undefined,
    onEscape: () => handleAction(cancelModalAction),
  });

  return (
    <div className="basic-entry-content">
      <Input
        inputRef={inputRef}
        className="basic-entry-input"
        inputMode={INPUT_MODES[valueType]}
        value={value}
        disabled={isSubmitting}
        error={validation.errorKey ? [t(validation.errorKey)] : null}
        onChange={(inputValue, event) =>
          setValue(
            valueType === 'string'
              ? inputValue
              : sanitizeEntryNumericValue(
                  valueType,
                  inputValue,
                  value,
                  getCursorPosition(event)
                )
          )
        }
        onKeyDown={(e) => {
          // The focused input swallows Enter before useModalKeyboard sees it.
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
      />
      <EntryActionButtons
        onConfirm={submit}
        confirmDisabled={!validation.isValid}
        onCancel={() => handleAction(cancelModalAction)}
      />
    </div>
  );
};

export default BasicEntryContent;
